import { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { CircularProgress } from "@mui/material"; // Import Loader
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import configs from "examples/Charts/BarCharts/VerticalBarChart/configs";
import colors from "assets/theme/base/colors";
import { useMaterialUIController } from "context";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function VerticalBarChart({
  icon,
  title,
  description,
  height,
  chart,
  type,
  loading,
  label
}) {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const safeData = Array.isArray(chart?.data) ? chart.data : [];

  const groupedData = safeData.reduce((acc, item) => {
    if (!item?.month || !item?.type) return acc;
    if (!acc[item.month]) {
      acc[item.month] = { PARKING: 0, SUBSCRIPTION: 0 };
    }
    acc[item.month][item.type] =
      type === "Revenue" ? item?.totalRevenue.toFixed(2) || 0 : item?.bookings || 0;
    return acc;
  }, {});

  const labels = Object.keys(groupedData);
  const parkingData = labels.map((month) => groupedData[month]?.PARKING || 0);
  const subscriptionData = labels.map(
    (month) => groupedData[month]?.SUBSCRIPTION || 0
  );
  const chartDatasets = [
    {
      // label: `Parking ${type}`,
      label: `${label} ${type}`,
      data: label === "Parking" ? parkingData : subscriptionData,
      backgroundColor: colors[sidenavColor].main,
      borderRadius: 4,
      maxBarThickness: 20,
    },
    // {
    //   label: `Subscription ${type}`,
    //   data: subscriptionData,
    //   backgroundColor: colors.dark.main,
    //   borderRadius: 4,
    //   maxBarThickness: 35,
    // },
  ];

  const { data, options } = configs(labels, chartDatasets, type);


  return (
    <Card>
      <MDBox py={2} pr={2} pl={icon?.component ? 1 : 2}>
        {(title || description) && (
          <MDBox
            display="flex"
            px={description ? 1 : 0}
            pt={description ? 1 : 0}
          >
            {icon?.component && (
              <MDBox
                width="4rem"
                height="4rem"
                bgColor={sidenavColor || "dark"}
                // bgColor={icon.color || "dark"}
                variant="gradient"
                coloredShadow={sidenavColor || "dark"}
                borderRadius="xl"
                display="flex"
                justifyContent="center"
                alignItems="center"
                color="white"
                mt={-5}
                mr={2}
              >
                <Icon fontSize="medium">{icon.component}</Icon>
              </MDBox>
            )}
            <MDBox mt={icon?.component ? -2 : 0}>
              {title && <MDTypography variant="h6">{title}</MDTypography>}
              <MDBox mb={2}>
                <MDTypography component="div" variant="button" color="text">
                  {description}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        )}
        {useMemo(
          () => (
            <MDBox
              height={height}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {loading ? (
                <CircularProgress color="primary" /> // Show Loader
              ) : safeData.length > 0 ? (
                <Bar data={data} options={options} redraw />
              ) : (
                <MDBox
                  height={height}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <MDTypography variant="button" color="text">
                    No data available
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          ),
          [loading, chart, height]
        )}
      </MDBox>
    </Card>
  );
}

VerticalBarChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "19.125rem",
  chart: { data: [] },
};

VerticalBarChart.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
    ]),
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        bookings: PropTypes.number,
        totalRevenue: PropTypes.number,
        month: PropTypes.string,
        type: PropTypes.string,
      })
    ),
  }),
  type: PropTypes.string.isRequired,
};

export default VerticalBarChart;
